import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, of } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { AuthenticationService, RoutingConfigService, SpinnerService } from '../_shared/services/services-index';
import { environment } from '../../environments/environment';
import { Store } from '@ngrx/store';
import { AppState } from '../_store/app-state.model';
import { AppActions } from '../_store/action-types';
import { FilterActions } from '../_shared/filter/store';

@Component({
    template: `
        <div class="text-center my-5">
            <h3>Processing impersonation...</h3>
            <p *ngIf="statusMessage && !errorMessage" class="mt-4">{{ statusMessage }}</p>
            <div *ngIf="errorMessage" class="mt-4 text-red-600">
                {{ errorMessage }}
            </div>
            <div *ngIf="isDebugMode" class="mt-6 text-xs bg-gray-100 p-4 rounded-md text-left max-w-xl mx-auto">
                <h4 class="font-semibold mb-2">Debug Details</h4>
                <pre class="whitespace-pre-wrap">{{ debugInfo | json }}</pre>
            </div>
            <div class="mt-4" *ngIf="errorMessage">
                <button class="btn btn-primary" (click)="navigateToLogin()">Back to Login</button>
            </div>
        </div>
    `,
    standalone: false
})
export class ImpersonateComponent implements OnInit, OnDestroy {
    statusMessage = '';
    errorMessage = '';
    isDebugMode = !environment.production;
    debugInfo: any = {};
    private subscription = new Subscription();

    constructor(
        private readonly route: ActivatedRoute,
        private readonly authService: AuthenticationService,
        private readonly spinner: SpinnerService,
        private readonly router: Router,
        private readonly store: Store<AppState>,
        private readonly routingConfigService: RoutingConfigService
    ) {}

    ngOnInit(): void {
        this.spinner.show();
        this.statusMessage = 'Validating parameters...';

        const params = this.route.snapshot.queryParamMap;
        const accessToken = params.get('accessToken')?.trim();
        const role = params.get('role')?.trim();
        const roleEntity = params.get('roleEntity')?.trim() || undefined;
        const userId = params.get('userId')?.trim() || undefined;

        this.debugInfo = {
            hasAccessToken: !!accessToken,
            role,
            roleEntity: roleEntity || null,
            userId: userId || null,
            timestamp: new Date().toISOString()
        };

        if (!accessToken || !role) {
            this.errorMessage = 'Missing required query parameters. "accessToken" and "role" are required.';
            this.statusMessage = '';
            this.spinner.hide();
            return;
        }

        // Clear any existing session before impersonating
        this.authService.resetUser();
        this.store.dispatch(FilterActions.resetAllReportViewFilters());

        this.statusMessage = `Requesting impersonation token for role "${role}"...`;

        const impersonation$ = this.authService.impersonate(accessToken, role, roleEntity, userId)
            .pipe(
                switchMap(response => this.authService.loginCustomToken(response.accessToken, '')),
                tap(() => {
                    this.statusMessage = 'Impersonation successful. Redirecting to application...';
                }),
                catchError(err => {
                    console.error('Impersonation error', err);
                    this.errorMessage = this.resolveErrorMessage(err);
                    this.statusMessage = '';
                    return of(null);
                }),
                finalize(() => {
                    this.spinner.hide();
                    if (!this.errorMessage) {
                        // Replace URL so the access token is not kept in history
                        void this.router.navigate([this.routingConfigService.defaultRoute], { replaceUrl: true });
                    }
                })
            );

        this.subscription.add(impersonation$.subscribe());
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    navigateToLogin(): void {
        void this.router.navigate(['/login']);
    }

    private resolveErrorMessage(err: any): string {
        if (err?.error?.detail) {
            return err.error.detail;
        }

        if (err?.message) {
            return err.message;
        }

        return 'Unable to complete impersonation. Please verify the query parameters and try again.';
    }
}
